import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom';
import '@/App.css';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SarahChat from '@/components/SarahChat';
import AccessibilityWidget from '@/components/AccessibilityWidget';
import HomePage from '@/pages/HomePage';
import CityPage from '@/pages/CityPage';
import IndustryHubPage from '@/pages/IndustryHubPage';
import StatePage from '@/pages/StatePage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import AboutPage from '@/pages/AboutPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/TermsPage';
import CitiesPage from '@/pages/CitiesPage';
import HotelSavingsCardPage from '@/pages/HotelSavingsCardPage';
import HotelBanner from '@/components/HotelBanner';
import StickyActionBar from '@/components/StickyActionBar';
import CookieConsent from '@/components/CookieConsent';
import CookiePolicyPage from '@/pages/CookiePolicyPage';
import DisclaimerPage from '@/pages/DisclaimerPage';
import AccessibilityPage from '@/pages/AccessibilityPage';
import ExitIntentPopup from '@/components/ExitIntentPopup';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function AppLayout({ children }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) return <>{children}</>;
  return (
    <>
      <Navigation />
      <AccessibilityWidget />
      <div id="main-wrapper" className="pt-[124px] pb-14">
        <main id="main-content">{children}</main>
        <Footer />
      </div>
      <StickyActionBar />
      <SarahChat />
      <CookieConsent />
      <ExitIntentPopup />
    </>
  );
}

function AdminRoute() {
  const [authed, setAuthed] = useState(false);
  const token = localStorage.getItem('admin_token');
  useEffect(() => { if (token) setAuthed(true); }, [token]);
  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;
  return <AdminDashboard onLogout={() => { localStorage.removeItem('admin_token'); setAuthed(false); }} />;
}

// Route parser for programmatic SEO URLs
function CatchAllRoute() {
  const { '*': path } = useParams();
  if (!path) return <HomePage />;

  // /workers-comp-insurance-california
  if (path === 'workers-comp-insurance-california') return <StatePage />;

  // /workers-comp-for-{industry}-companies
  const hubMatch = path.match(/^workers-comp-for-([\w-]+)-companies$/);
  if (hubMatch) return <IndustryHubPage key={hubMatch[1]} />;

  // /workers-comp-for-{industry}-in-{city}-ca
  const cityIndMatch = path.match(/^workers-comp-for-([\w-]+)-in-([\w-]+)-ca$/);
  if (cityIndMatch) return <CityPage key={path} />;

  // /workers-comp-insurance-{city}-ca
  const cityMatch = path.match(/^workers-comp-insurance-([\w-]+)-ca$/);
  if (cityMatch) return <CityPage key={path} />;

  return <div className="min-h-screen flex items-center justify-center text-gray-500 pt-20">Page not found</div>;
}

// Custom hook to extract city/industry from URL
export function usePageParams() {
  const location = useLocation();
  const path = location.pathname.replace(/^\//, '');

  // /workers-comp-for-{industry}-in-{city}-ca
  const cityIndMatch = path.match(/^workers-comp-for-([\w-]+)-in-([\w-]+)-ca$/);
  if (cityIndMatch) return { industrySlug: cityIndMatch[1], citySlug: cityIndMatch[2] };

  // /workers-comp-insurance-{city}-ca
  const cityMatch = path.match(/^workers-comp-insurance-([\w-]+)-ca$/);
  if (cityMatch) return { citySlug: cityMatch[1], industrySlug: null };

  // /workers-comp-for-{industry}-companies
  const hubMatch = path.match(/^workers-comp-for-([\w-]+)-companies$/);
  if (hubMatch) return { industrySlug: hubMatch[1], citySlug: null };

  return { citySlug: null, industrySlug: null };
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/admin" element={<AdminRoute />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/cities" element={<CitiesPage />} />
          <Route path="/hotel-savings-card" element={<HotelSavingsCardPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/terms-of-service" element={<TermsPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/*" element={<CatchAllRoute />} />
        </Routes>
      </AppLayout>
    </BrowserRouter>
  );
}

export default App;
